import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty, Stat } from "@/components/app/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Target, Users, Grid3x3, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { hasAnyRole } from "@/lib/perms";

const err = (e) => toast.error(formatErrorDetail(e.response?.data?.detail) || "Something went wrong");

function useEmployees() {
    const [emps, setEmps] = useState([]);
    useEffect(() => { api.get("/hris/employees", { params: { limit: 500 } }).then((r) => setEmps(r.data.items || [])).catch(() => {}); }, []);
    const map = Object.fromEntries(emps.map((e) => [e.id, `${e.last_name}, ${e.first_name}`]));
    return { emps, map };
}

/* ---------------- Goals / OKRs ---------------- */
function GoalsTab() {
    const [goals, setGoals] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", type: "objective", weight: "", due_date: "", key_results: [{ title: "", target: "", current: "" }] });
    const load = () => api.get("/performance/me/goals").then((r) => setGoals(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);

    const addKr = () => setForm({ ...form, key_results: [...form.key_results, { title: "", target: "", current: "" }] });
    const save = async () => {
        if (!form.title.trim()) return toast.error("Goal title is required");
        try {
            await api.post("/performance/goals", { ...form, weight: Number(form.weight || 0), key_results: form.key_results.filter((k) => k.title.trim()) });
            toast.success("Goal created"); setOpen(false);
            setForm({ title: "", description: "", type: "objective", weight: "", due_date: "", key_results: [{ title: "", target: "", current: "" }] });
            load();
        } catch (e) { err(e); }
    };
    const bumpKr = async (goal, ki, done) => {
        const krs = goal.key_results.map((k, i) => i === ki ? { ...k, done } : k);
        try { await api.put(`/performance/goals/${goal.id}`, { key_results: krs, status: "in_progress" }); load(); } catch (e) { err(e); }
    };
    const setStatus = async (goal, status) => { try { await api.put(`/performance/goals/${goal.id}`, { status }); load(); } catch (e) { err(e); } };

    return (
        <div>
            <div className="flex justify-end my-2">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2" data-testid="new-goal-btn"><Plus className="size-4" /> New Goal</button></DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader><DialogTitle>New Goal / Objective</DialogTitle></DialogHeader>
                        <div className="space-y-2">
                            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="goal-title" /></div>
                            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                            <div className="grid grid-cols-3 gap-2">
                                <div><Label>Type</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="objective">Objective</option><option value="kpi">KPI</option></select></div>
                                <div><Label>Weight %</Label><Input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></div>
                                <div><Label>Due</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                            </div>
                            <div>
                                <Label>Key Results</Label>
                                {form.key_results.map((k, i) => (
                                    <div key={i} className="grid grid-cols-3 gap-2 mt-1">
                                        <Input placeholder="Key result" value={k.title} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} />
                                        <Input placeholder="Target" value={k.target} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, j) => j === i ? { ...x, target: e.target.value } : x) })} />
                                        <Input placeholder="Current" value={k.current} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, j) => j === i ? { ...x, current: e.target.value } : x) })} />
                                    </div>
                                ))}
                                <button className="text-xs text-primary mt-1" onClick={addKr}>+ Add key result</button>
                            </div>
                        </div>
                        <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="goal-save">Create Goal</button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="space-y-3">
                {goals.map((g) => (
                    <Surface key={g.id} className="p-4" data-testid={`goal-${g.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-semibold flex items-center gap-2">{g.title}<span className="badge badge-info text-[10px] capitalize">{g.type}</span>{g.weight ? <span className="text-xs text-muted-foreground">· {g.weight}%</span> : null}</div>
                                {g.description && <div className="text-sm text-muted-foreground mt-0.5">{g.description}</div>}
                            </div>
                            <select className="h-8 px-2 rounded-md border border-border text-xs capitalize" value={g.status} onChange={(e) => setStatus(g, e.target.value)}>
                                {["not_started", "in_progress", "at_risk", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                            </select>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary" style={{ width: `${g.progress || 0}%` }} /></div>
                        <div className="text-xs text-muted-foreground mt-1">{g.progress || 0}% complete{g.due_date ? ` · due ${g.due_date}` : ""}</div>
                        {!!(g.key_results || []).length && (
                            <div className="mt-2 space-y-1">
                                {g.key_results.map((k, i) => (
                                    <label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!k.done} onChange={(e) => bumpKr(g, i, e.target.checked)} /> <span className={k.done ? "line-through text-muted-foreground" : ""}>{k.title}{k.target ? ` (${k.current || 0}/${k.target})` : ""}</span></label>
                                ))}
                            </div>
                        )}
                    </Surface>
                ))}
                {!goals.length && <Empty title="No goals yet" subtitle="Set your objectives and key results for this period." />}
            </div>
        </div>
    );
}

/* ---------------- Review Cycles (360 + 9-box) ---------------- */
function CyclesTab() {
    const { emps, map } = useEmployees();
    const [cycles, setCycles] = useState([]);
    const [sel, setSel] = useState(null);
    const [result, setResult] = useState(null);
    const [grid, setGrid] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", period_start: "", period_end: "", all_companies: false, competencies: "Job Knowledge, Quality of Work, Collaboration, Initiative, Communication" });
    const [subject, setSubject] = useState("");
    const [peer, setPeer] = useState("");

    const load = () => api.get("/performance/review-cycles").then((r) => setCycles(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name.trim()) return toast.error("Cycle name is required");
        try { await api.post("/performance/review-cycles", { ...form, competencies: form.competencies.split(",").map((c) => c.trim()).filter(Boolean) }); toast.success("Cycle created"); setOpen(false); load(); } catch (e) { err(e); }
    };
    const launch = async () => { if (!sel || !subject) return; try { const r = await api.post(`/performance/review-cycles/${sel.id}/launch`, { employee_id: subject }); toast.success(`Launched ${r.data.created} review(s)`); } catch (e) { err(e); } };
    const reqPeer = async () => { if (!sel || !subject || !peer) return; try { await api.post(`/performance/review-cycles/${sel.id}/request-peer`, { employee_id: subject, reviewer_ids: [peer] }); toast.success("Peer review requested"); } catch (e) { err(e); } };
    const viewResult = async () => { if (!sel || !subject) return; try { const r = await api.get(`/performance/review-cycles/${sel.id}/result`, { params: { employee_id: subject } }); setResult(r.data); } catch (e) { err(e); } };
    const setPotential = async (p) => { if (!sel || !subject) return; try { await api.post(`/performance/review-cycles/${sel.id}/potential`, { employee_id: subject, potential: p }); toast.success("Potential set"); } catch (e) { err(e); } };
    const loadGrid = async (c) => { setSel(c); try { const r = await api.get(`/performance/review-cycles/${c.id}/9box`); setGrid(r.data); } catch (e) { err(e); } };

    return (
        <div className="grid lg:grid-cols-3 gap-3 mt-2">
            <Surface className="p-3 lg:col-span-1">
                <div className="flex items-center justify-between mb-2"><h3 className="font-semibold text-sm">Review Cycles</h3>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><button className="text-xs text-primary" data-testid="new-cycle-btn">+ New</button></DialogTrigger>
                        <DialogContent><DialogHeader><DialogTitle>New Review Cycle</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2026 Mid-Year Review" data-testid="cycle-name" /></div>
                                <div className="grid grid-cols-2 gap-2"><div><Label>Start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} /></div><div><Label>End</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} /></div></div>
                                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.all_companies} onChange={(e) => setForm({ ...form, all_companies: e.target.checked })} data-testid="cycle-all-companies" /> Apply to ALL companies (no per-company rework)</label>
                                <div><Label>Competencies (comma-separated)</Label><Input value={form.competencies} onChange={(e) => setForm({ ...form, competencies: e.target.value })} /></div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="cycle-save">Create</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
                <div className="space-y-1">
                    {cycles.map((c) => (
                        <button key={c.id} onClick={() => { setSel(c); setResult(null); setGrid(null); }} className={`w-full text-left px-2 py-1.5 rounded-md text-sm ${sel?.id === c.id ? "bg-secondary font-medium" : ""}`} data-testid={`cycle-${c.id}`}>
                            {c.name} <span className="text-xs text-muted-foreground">· {c.status}</span>
                        </button>
                    ))}
                    {!cycles.length && <div className="text-xs text-muted-foreground">No cycles yet.</div>}
                </div>
            </Surface>

            <Surface className="p-4 lg:col-span-2">
                {!sel ? <Empty title="Select a cycle" subtitle="Launch 360 reviews, request peer feedback, and calibrate." /> : (
                    <div className="space-y-3">
                        <div className="font-semibold">{sel.name}</div>
                        <div className="grid md:grid-cols-2 gap-2">
                            <div><Label>Employee under review</Label>
                                <select className="w-full h-10 px-3 rounded-md border border-border" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="cycle-subject">
                                    <option value="">Select…</option>{emps.map((e) => <option key={e.id} value={e.id}>{map[e.id]}</option>)}
                                </select>
                            </div>
                            <div className="flex items-end gap-2"><button className="h-10 px-3 rounded-md bg-primary text-white text-sm" onClick={launch} data-testid="cycle-launch">Launch self + manager</button><button className="h-10 px-3 rounded-md border border-border text-sm" onClick={viewResult}>View result</button></div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-2">
                            <div className="flex items-end gap-2">
                                <div className="flex-1"><Label>Add peer reviewer</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={peer} onChange={(e) => setPeer(e.target.value)}><option value="">Select…</option>{emps.map((e) => <option key={e.id} value={e.id}>{map[e.id]}</option>)}</select>
                                </div>
                                <button className="h-10 px-3 rounded-md border border-border text-sm" onClick={reqPeer}>Request 360</button>
                            </div>
                            <div><Label>Potential (9-box)</Label><div className="flex gap-2 mt-1">{[1, 2, 3].map((p) => <button key={p} className="h-10 flex-1 rounded-md border border-border text-sm" onClick={() => setPotential(p)}>{["Low", "Medium", "High"][p - 1]}</button>)}</div></div>
                        </div>
                        {result && (
                            <div className="rounded-md border border-border p-3 text-sm" data-testid="cycle-result">
                                <div className="font-medium mb-1">360 Result — final rating: <b>{result.final_rating ?? "—"}</b></div>
                                <div className="text-muted-foreground">Self {result.self_rating ?? "—"} · Manager {result.manager_rating ?? "—"} · Peer {result.peer_rating ?? "—"} · {result.reviews_submitted}/{result.reviews_total} reviews submitted</div>
                            </div>
                        )}
                        <button className="text-xs text-primary flex items-center gap-1" onClick={() => loadGrid(sel)}><Grid3x3 className="size-3.5" /> Load 9-box grid</button>
                        {grid && <NineBox grid={grid} />}
                    </div>
                )}
            </Surface>
        </div>
    );
}

function NineBox({ grid }) {
    const labelsPerf = ["Low", "Med", "High"];
    const labelsPot = ["Low", "Med", "High"];
    return (
        <div className="mt-2">
            <div className="text-xs text-muted-foreground mb-1">Performance (→) × Potential (↑) · {grid.placed} placed</div>
            <div className="grid grid-cols-3 gap-1" data-testid="ninebox">
                {[3, 2, 1].map((pot) => labelsPerf.map((_, pi) => {
                    const perf = pi + 1;
                    const cell = grid.grid[`${perf},${pot}`] || [];
                    const hot = perf === 3 && pot === 3;
                    return <div key={`${perf}-${pot}`} className={`min-h-16 rounded-md border p-1.5 text-[11px] ${hot ? "bg-emerald-50 border-emerald-200" : "bg-secondary/40 border-border"}`}>
                        <div className="text-[9px] uppercase text-muted-foreground">P{perf}/Pot{pot}</div>
                        {cell.slice(0, 4).map((e) => <div key={e.employee_id} className="truncate">{e.name}</div>)}
                        {cell.length > 4 && <div className="text-muted-foreground">+{cell.length - 4} more</div>}
                    </div>;
                }))}
            </div>
        </div>
    );
}

/* ---------------- My Reviews (fill as reviewer) ---------------- */
function MyReviewsTab() {
    const { map } = useEmployees();
    const [reviews, setReviews] = useState([]);
    const [active, setActive] = useState(null);
    const load = () => api.get("/performance/me/reviews").then((r) => setReviews(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);
    const submit = async () => {
        try { await api.post(`/performance/reviews/${active.id}/submit`, { competency_ratings: active.competency_ratings, strengths: active.strengths, improvements: active.improvements }); toast.success("Review submitted"); setActive(null); load(); } catch (e) { err(e); }
    };
    return (
        <div className="mt-2">
            <TableWrap>
                <thead><tr><th>Employee</th><th>Cycle</th><th>Type</th><th>Status</th><th></th></tr></thead>
                <tbody>
                    {reviews.map((r) => (
                        <tr key={r.id} data-testid={`myreview-${r.id}`}>
                            <td>{map[r.employee_id] || r.employee_id?.slice(0, 8)}</td>
                            <td>{r.cycle_name}</td>
                            <td className="capitalize">{r.reviewer_type}</td>
                            <td>{r.status === "submitted" ? <span className="badge badge-approved">submitted</span> : <span className="badge badge-draft">pending</span>}</td>
                            <td>{r.status !== "submitted" && <button className="text-xs text-primary" onClick={() => setActive(r)}>Complete</button>}</td>
                        </tr>
                    ))}
                    {!reviews.length && <tr><td colSpan={5}><Empty title="No reviews assigned to you" /></td></tr>}
                </tbody>
            </TableWrap>
            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle>Review — {active?.cycle_name}</DialogTitle></DialogHeader>
                    {active && (
                        <div className="space-y-2 max-h-[60vh] overflow-auto">
                            {active.competency_ratings.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 gap-2 items-center">
                                    <div className="text-sm">{c.competency}</div>
                                    <select className="h-9 px-2 rounded-md border border-border text-sm" value={c.rating ?? ""} onChange={(e) => setActive({ ...active, competency_ratings: active.competency_ratings.map((x, j) => j === i ? { ...x, rating: e.target.value === "" ? null : Number(e.target.value) } : x) })}>
                                        <option value="">Rate…</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <Input placeholder="comment" value={c.comment || ""} onChange={(e) => setActive({ ...active, competency_ratings: active.competency_ratings.map((x, j) => j === i ? { ...x, comment: e.target.value } : x) })} />
                                </div>
                            ))}
                            <div><Label>Strengths</Label><Input value={active.strengths || ""} onChange={(e) => setActive({ ...active, strengths: e.target.value })} /></div>
                            <div><Label>Areas to improve</Label><Input value={active.improvements || ""} onChange={(e) => setActive({ ...active, improvements: e.target.value })} /></div>
                        </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid="review-submit">Submit Review</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function Performance() {
    const { user, activeCompanyId } = useAuth();
    const isManager = hasAnyRole(user, activeCompanyId, ["super_admin", "hr_admin", "hr_staff", "manager", "general_manager"]);
    return (
        <div>
            <PageHeader title="Performance" subtitle="Goals & OKRs, 360° review cycles, and 9-box talent calibration." />
            <Tabs defaultValue="goals">
                <TabsList>
                    <TabsTrigger value="goals" data-testid="perf-tab-goals"><Target className="size-4 mr-1" /> My Goals</TabsTrigger>
                    <TabsTrigger value="reviews" data-testid="perf-tab-reviews"><ClipboardCheck className="size-4 mr-1" /> My Reviews</TabsTrigger>
                    {isManager && <TabsTrigger value="cycles" data-testid="perf-tab-cycles"><Users className="size-4 mr-1" /> Review Cycles</TabsTrigger>}
                </TabsList>
                <TabsContent value="goals"><GoalsTab /></TabsContent>
                <TabsContent value="reviews"><MyReviewsTab /></TabsContent>
                {isManager && <TabsContent value="cycles"><CyclesTab /></TabsContent>}
            </Tabs>
        </div>
    );
}
